<template>
  <div class="kpi-card-alternative">
    <!-- Meta Information Header -->
    <div class="kpi-meta">
      <span v-if="scenario" class="meta-item">{{ scenario }}</span>
      <span v-if="scenario" class="meta-divider"></span>
      <span v-if="KPI.wave(kpi)" class="meta-item">{{ KPI.wave(kpi) }}</span>
      <span v-if="KPI.wave(kpi)" class="meta-divider"></span>
      <span v-if="KPI.state(kpi)" class="meta-item">{{ KPI.state(kpi) }}</span>
      <span v-if="KPI.state(kpi)" class="meta-divider"></span>
      <span v-if="KPI.country(kpi)" class="meta-item">{{ KPI.country(kpi) }}</span>
    </div>

    <!-- KPI Type Tabs -->
    <div class="kpi-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        class="kpi-tab"
        :class="{ 'active': activeTab === tab.value }"
        @click="activeTab = tab.value">
        <span class="tab-label">{{ tab.label || 'QQ' }}</span>
      </button>
    </div>

    <!-- Main Score Display -->
    <div class="kpi-score-container">
      <div class="score-main">
        <div class="score-value-wrapper">
          <div class="score-number-group">
            <span class="score-value">{{ KPI.lastResult(kpi) }}</span>
          </div>
          <div class="score-target" v-if="KPI.lastResultTarget(kpi)">
            <span class="target-label">{{ $t('Webportal.Target') }}</span>
            <span class="target-value">{{ KPI.lastResultTarget(kpi) }}</span>
          </div>
        </div>
        <span class="score-unit">%</span>
      </div>

      <!-- Badges & Previous Wave Info -->
      <div class="kpi-info">
        <div v-if="delta !== null" class="delta-badge" :class="deltaClass">
          <span>{{ deltaPrefix }}{{ Math.abs(delta) }}</span>
        </div>
        <div class="previous-wave" v-if="KPI.secondLastResult(kpi) !== null">
          <span class="prev-label">{{ $t('Webportal.Prewave') }}</span>
          <span class="prev-value">{{ KPI.secondLastResult(kpi) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useKpiHelpers } from '@/composables/kpi-helpers';

export default {
  name: 'KpiCardAlternative',

  props: {
    kpi: {
      type: Object,
      required: true
    },
    score: {
      type: Number,
      required: true
    },
    target: {
      type: Number,
      default: null
    },
    scenario: {
      type: String,
      default: 'QQ'
    },
    tabs: {
      type: Array,
      default: () => [
        { label: 'Contact', value: 'contact' },
        { label: 'Personal response', value: 'personal' }
      ]
    },
    defaultTab: {
      type: String,
      default: 'personal'
    }
  },

  setup() {
    const KPI = useKpiHelpers();
    return { KPI };
  },

  data() {
    return {
      activeTab: this.defaultTab
    };
  },

  mounted() {
    console.log("MN: ", this.KPI.lastResult(this.kpi));
  },
  computed: {
    formattedTarget() {
      return this.target !== null ? Math.round(this.target) : null;
    },

    delta() {
      if (this.previousWaveScore === null) return null;
      return this.score - this.previousWaveScore;
    },

    deltaPrefix() {
      return this.delta > 0 ? '+' : '';
    },

    deltaClass() {
      if (this.delta === null) return '';
      if (this.delta > 0) return 'positive';
      if (this.delta < 0) return 'negative';
      return 'neutral';
    }
  }
};
</script>

<style lang="scss" scoped>
.kpi-card-alternative {
  border: 1px solid var(--color-border);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transform: skewX(var(--target-skew-angle-inverse));
  position: relative;

  > * {
    transform: skewX(var(--target-skew-angle));
  }

}

.kpi-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  height: 16px;
}

.meta-item {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 10px;
  line-height: 10px;
  color: var(--color-foreground);
  white-space: nowrap;
}

.meta-divider {
  width: 2px;
  height: 16px;
  border: 1px solid var(--color-accent);
}

.kpi-tabs {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  padding: 0 0 0 4.5px;
}

.kpi-tab {
  background: rgba(255, 255, 255, 0);
  border: 1px solid  var(--color-border);
  padding: 4px 8px;
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 10px;
  line-height: 16px;
  color: var(--color-muted-foreground);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  transform: skewX(var(--target-skew-angle-inverse));

  &:hover {
    background: var(--color-accent);
  }

  &.active {
    background: var(--color-accent);
    color: var(--color-foreground);
  }

  &:first-child::after {
    content: '';
    display: inline-block;
    width: 16px;
    height: 2px;
    background: var(--color-muted-foreground);
    margin-right: 8px;
    vertical-align: middle;
  }
}
.tab-label {
  transform: skewX(-45deg);
}

.kpi-score-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.score-main {
  display: flex;
  align-items: flex-end;
  gap: 0px;
  font-weight: 500;
}

.score-value-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
}

.score-number-group {
  display: flex;
  align-items: flex-end;
  gap: 4px;
}

.score-value {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 48px;
  line-height: 48px;
  color: var(--target-primary);
  letter-spacing: 0.3955px;
  white-space: nowrap;
}

.score-unit {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 24px;
  line-height: 60px;
  color: var(--target-primary);
  letter-spacing: 0.3955px;
  white-space: nowrap;
}

.score-target {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 0 4px;
  width: 100%;
}

.target-label {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-muted-foreground);
  white-space: nowrap;
}

.target-value {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--default-status-green);
  white-space: nowrap;
}

.kpi-info {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: 64px;
  padding-top: 4px;
}

.delta-badge {
  background: rgba(0, 201, 80, 0.1);
  border: 1px solid red; // rgba(0, 201, 80, 0.1);
  padding: 8px 12px;
  transform: skewX(var(--target-skew-angle-inverse));

  span {
    transform: skewX(var(--target-skew-angle));
    display: inline-block;
    font-family: 'BMWTypeNext Pro', sans-serif;
    font-size: 12px;
    line-height: 16px;
    color: var(--default-status-green);
    white-space: nowrap;
  }

  &.positive {
    background: rgba(0, 201, 80, 0.1);
    border-color: rgba(0, 201, 80, 0.1);

    span {
      color: var(--default-status-green);
    }
  }

  &.negative {
    background: rgba(255, 21, 0, 0.1);
    border-color: rgba(255, 21, 0, 0.1);

    span {
      color: var(--default-status-red);
    }
  }

  &.neutral {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);

    span {
      color: var(--default-status-indifferent);
    }
  }
}

.previous-wave {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding-right: 15px;
  width: 100%;
}

.prev-label {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-muted-foreground);
  white-space: nowrap;
}

.prev-value {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-foreground);
  white-space: nowrap;
}

:root.dark,
[data-bs-theme="dark"] {
  .kpi-card-alternative {
    background: var(--color-card);
    border-color: var(--color-border);
  }
}
</style>
